/**
 * AdminModerationToggleCard — pause/resume the platform-wide AI caption
 * moderation that runs on every vendor image upload. Default is enabled;
 * pause only when the Gemini quota is exhausted.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, ShieldSlash, Spinner } from '@phosphor-icons/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

async function fetchStatus() {
  const { data } = await axios.get(
    `${BACKEND_URL}/api/admin/moderation-status`,
    { withCredentials: true },
  );
  return data.enabled !== false;
}

async function putStatus(target) {
  const { data } = await axios.put(
    `${BACKEND_URL}/api/admin/moderation-status`,
    { enabled: target },
    { withCredentials: true },
  );
  return data.enabled !== false;
}

const AdminModerationToggleCard = () => {
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    fetchStatus()
      .then((v) => { if (mounted) { setEnabled(v); setLoading(false); } })
      .catch((err) => {
        if (mounted) {
          toast.error(err?.response?.data?.detail || 'Failed to load moderation status');
          setLoading(false);
        }
      });
    return () => { mounted = false; };
  }, []);

  const onToggle = () => {
    const target = !enabled;
    setSaving(true);
    putStatus(target)
      .then((v) => {
        setEnabled(v);
        toast.success(v ? 'AI moderation enabled' : 'AI moderation paused');
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail || 'Failed to update');
      })
      .finally(() => setSaving(false));
  };

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="admin-moderation-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          {enabled
            ? <ShieldCheck size={20} className="text-emerald-600" weight="fill" />
            : <ShieldSlash size={20} className="text-red-600" weight="fill" />}
          AI Caption Moderation
        </CardTitle>
        <CardDescription>
          Gemini Vision rejects any vendor-uploaded image with caption text /
          overlays. Applies platform-wide to portfolio, vendor gallery, decor
          packages, and the generic upload endpoint. Pause only if the Gemini
          quota is exhausted.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div
          className="flex items-center justify-between gap-3 p-3 rounded border-2"
          style={{
            borderColor: enabled ? '#86EFAC' : '#FCA5A5',
            background: enabled ? '#F0FDF4' : '#FEF2F2',
          }}
          data-testid="admin-moderation-row"
        >
          <div className="flex items-center gap-2">
            <span
              className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                enabled ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'
              }`}
              data-testid="admin-moderation-pill"
            >
              {loading ? '…' : (enabled ? 'Enabled' : 'Paused')}
            </span>
            <span className="text-sm">
              {enabled ? 'All vendor uploads are being scanned.' : 'Uploads are bypassing moderation.'}
            </span>
          </div>
          <Button
            onClick={onToggle}
            disabled={loading || saving}
            variant="outline"
            className="shrink-0"
            data-testid="admin-moderation-toggle-btn"
          >
            {saving ? <Spinner size={14} className="animate-spin mr-2" /> : null}
            {enabled ? 'Pause' : 'Resume'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminModerationToggleCard;
